/**
 * 销冠键盘 · 截图 OCR 识别
 * 流程：
 *   1. 图片转 base64（纯 base64，不带 data:image/...;base64, 前缀）
 *   2. 构造火山 OCR 表单 body，计算 bodyHash = HexEncode(SHA256(body))
 *   3. 调 /group/web/ocr/getVolcengine.do 获取签名凭证（X-Date / Authorization）
 *   4. 调火山通用文字识别 OCRNormal，返回识别文本
 */
import axios from 'axios';
import CryptoJS from 'crypto-js';
import http from '@/servers/http';
import common from '@/common/js/common';
import config from '@/config/config';

const OCR_HOST = 'https://visual.volcengineapi.com';
const OCR_ACTION = 'OCRNormal';
const OCR_VERSION = '2020-08-26';

// 火山图片大小限制：最大 5MB
export const OCR_IMAGE_MAX_SIZE = 5 * 1024 * 1024;

function sha256Hex(str) {
  return CryptoJS.SHA256(str).toString(CryptoJS.enc.Hex);
}

// 把完整 data 落到 /tmp，便于直接读取原始密文做离线解密排查
function dumpOcrDebugFile(raw) {
  try {
    if (typeof window !== 'undefined' && window.require) {
      const fs = window.require('fs');
      fs.writeFileSync('/tmp/ocr_raw_data.txt', raw, 'utf8');
      console.log('[ocr] 完整 data 已写入 /tmp/ocr_raw_data.txt');
    }
  } catch (e) {
    console.error('[ocr] 写入 /tmp/ocr_raw_data.txt 失败:', e.message);
  }
}

/**
 * 去掉 dataURL 前缀，仅保留纯 base64
 * @param {string} dataUrl - "data:image/png;base64,xxxx"
 * @returns {string}
 */
export function stripBase64(dataUrl) {
  if (typeof dataUrl !== 'string') return '';
  const idx = dataUrl.indexOf(',');
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}

/**
 * 解析 getVolcengine.do 返回的 data → { X-Date, Authorization }
 * data 可能是：对象 / 明文 JSON 字符串 / 固定 key AES 加密的 base64 字符串
 */
function parseCredential(data, key) {
  if (data && typeof data === 'object') {
    return data.object || data;
  }
  if (typeof data !== 'string' || !data) {
    throw new Error('OCR 凭证为空');
  }
  const raw = data.trim();

  // 1) 明文 JSON 字符串
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed.object || parsed;
    }
  } catch (_) {}

  // 2) AES 加密：优先用户 key（与请求同一把），再尝试 config.ak 与默认 key
  const keys = [...new Set([key, config.ak, '1234567890123456'])];
  for (const k of keys) {
    let decrypted = '';
    try {
      decrypted = common.decrypt(raw, k);
    } catch (_) {
      continue;
    }
    if (!decrypted) continue;
    try {
      const parsed = JSON.parse(decrypted);
      return (parsed && parsed.object) || parsed || {};
    } catch (_) {
      continue;
    }
  }

  console.error('[ocr] 凭证解密失败，完整 data:', raw);
  dumpOcrDebugFile(raw);
  throw new Error('OCR 凭证解密失败，完整数据已写入 /tmp/ocr_raw_data.txt');
}

/**
 * 识别图片文字
 * @param {string} base64 - 纯 base64（不带 data: 前缀）
 * @returns {Promise<string>} 识别出的文本，按行以 \n 拼接
 */
export async function recognizeImageText(base64) {
  if (!base64) {
    throw new Error('图片为空');
  }

  // 1. 火山 OCR 请求体（x-www-form-urlencoded），bodyHash = HexEncode(SHA256(body))
  const body = `image_base64=${encodeURIComponent(base64)}`;
  const bodyHash = sha256Hex(body);

  // 2. 获取火山 OCR 签名凭证
  const resp = await http.getVolcengineOcr({ bodyHash });
  console.log('[ocr] getVolcengine.do 返回:', JSON.stringify(resp));
  if (!resp || resp.code !== 200) {
    throw new Error((resp && resp.message) || '获取 OCR 凭证失败');
  }

  // 解密 key 与请求加密一致：localStorage 的 edujia:group:web:key，回退 config.ak
  let aesKey = config.ak;
  try {
    aesKey = localStorage.getItem('edujia:group:web:key') || config.ak;
  } catch (_) {}

  const obj = parseCredential(resp.data, aesKey);
  const xDate = obj['X-Date'];
  const authorization = obj['Authorization'];
  if (!xDate || !authorization) {
    throw new Error('OCR 凭证缺失');
  }

  // 3. 调用火山通用文字识别
  const ocrResp = await axios.post(
    `${OCR_HOST}/?Action=${OCR_ACTION}&Version=${OCR_VERSION}`,
    body,
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Date': xDate,
        'x-content-sha256': bodyHash,
        'Authorization': authorization,
      },
      timeout: 30000,
    }
  );

  const data = ocrResp.data;
  if (
    data &&
    data.code === 10000 &&
    data.data &&
    Array.isArray(data.data.line_texts)
  ) {
    return data.data.line_texts.join('\n');
  }
  throw new Error((data && data.message) || 'OCR 识别失败');
}
